import type { Timestamp } from "firebase/firestore";
import { VALE_FALTAS_INICIAIS } from "@nhomi/core";
import type {
  AppDeDelivery, Canal, ChallengeConfig, DayKey, EventState, Lembretes, Marca,
} from "@nhomi/core";

/**
 * O formato dos documentos no Firestore.
 *
 * Separado dos tipos do `@nhomi/core` de propósito: o core trabalha com
 * números e strings puros, e aqui tem `Timestamp`, campos opcionais e o
 * lixo normal de um banco que evolui. Quem traduz de um para o outro são
 * os adaptadores — é o que impede o formato do banco de vazar para dentro
 * das regras de negócio.
 *
 * Modelo:
 *   users/{uid}                              perfil
 *   users/{uid}/blocks/{uid}                 quem essa pessoa bloqueou
 *   challenges/{cid}                         desafio + configuração de pontos
 *   challenges/{cid}/members/{uid}           ficha: placar (escrito pelo servidor)
 *   challenges/{cid}/plates/{pid}            prato
 *   challenges/{cid}/plates/{pid}/ratings/{uid}   nota — só quem deu enxerga
 *   challenges/{cid}/plates/{pid}/guesses/{uid}   palpite de preço/caloria
 *   challenges/{cid}/plates/{pid}/reactions/{uid} reação, uma por pessoa
 *   codes/{CODE}                             código de convite -> cid
 */

export interface UserDoc {
  name: string;
  photo: string;
  email: string;
  /** Apelido escolhido à mão: impede o nome do Google de sobrescrever. */
  nickname?: boolean;
  customPhoto?: boolean;
  challengeIds: string[];
  /** Escrito só pelo servidor. Ver `packages/core/src/plano.ts`. */
  plano?: "gratis" | "assinante";
  createdAt?: Timestamp;
  /** Quais avisos esta pessoa quer receber. Conferido no SERVIDOR. */
  notify?: {
    plates: boolean;
    comments: boolean;
    ratings: boolean;
    chat: boolean;
    guesses?: boolean;
    disputa?: boolean;
    missoes?: boolean;
  };
  /** Grupos de que a pessoa não quer aviso nenhum. Conferido no SERVIDOR. */
  silenciados?: string[];

  /* ---- lembretes de refeição ----

     Moram AQUI, e não no desafio, porque lembrete não é evento de grupo:
     quem está em dez desafios quer um aviso de almoço, não dez. Ver
     `packages/core/src/lembrete.ts`. */
  lembretes?: Lembretes;
  /** Os horários ligados em UTC — endereço, não permissão. Ver `minutosUTC`. */
  lembretesUTC?: string[];
}

/** Um aparelho registrado para receber notificação. */
export interface PushTokenDoc {
  token: string;
  platform: string;
  standalone: boolean;
  updatedAt?: Timestamp;
}

export interface ChallengeDoc extends Partial<ChallengeConfig> {
  name: string;
  description: string;
  /** Guardadas como DayKey, não Timestamp: desafio é coisa de calendário,
      e Timestamp obrigaria a decidir fuso a cada leitura. */
  startDay: DayKey;
  endDay: DayKey;
  code: string;
  /** Quem pode comentar e dar nota. Ausente = "todos". Ver `Interacao` no core. */
  interacao?: "todos" | "dono" | "ninguem";
  /** Removidos com "não deixar voltar". Só o servidor escreve. */
  banidos?: string[];
  ownerUid: string;
  memberUids: string[];
  coverPath?: string | null;
  createdAt?: Timestamp;
}

export interface MemberDoc {
  name: string;
  photo: string;
  role: "owner" | "member";
  joinedAt?: Timestamp;

  /* ---- daqui para baixo, só o servidor escreve ---- */
  days: DayKey[];
  dayPoints: Record<DayKey, number>;
  extraPoints: Record<DayKey, number>;
  totalPoints: number;
  events: Record<string, EventState>;
  cuisineIds: string[];
  plateCount: number;
  homemadeCount: number;
  boughtCount: number;
  jokerDays: DayKey[];
  jokers: number;
  /** Cravadas e comentários em prato alheio, por dia — para as missões. Só o servidor escreve. */
  atividade?: Partial<Record<"cravar" | "comentar", Record<DayKey, number>>>;
  /** Segunda-feira da semana → pontos trocados por vale-falta. Só o servidor escreve. */
  trocas?: Record<DayKey, number>;
}

export interface PlateDoc {
  uid: string;
  authorName: string;
  authorPhoto: string;
  title: string;
  description: string;
  mealId: string | null;
  cuisineId: string | null;
  homemade: boolean;

  /* Caminhos no Cloud Storage — nunca a imagem em si.

     `photoPath`/`thumbPath` são a foto de CAPA, e continuam existindo
     mesmo com várias fotos: o feed, o guia e o calendário mostram uma só,
     e fazê-los cavar dentro de `photos` custaria uma ramificação em cada
     lugar que desenha miniatura. São sempre iguais a `photos[0]`. */
  photoPath: string | null;
  thumbPath: string | null;
  /** Todas as fotos do prato, na ordem em que foram tiradas. */
  photos?: { photo: string; thumb: string }[];
  /** Postado sem foto (set/2026): custa `semFotoCusto` no placar. A
      regra do Firestore obriga o campo a dizer a verdade na criação e o
      tranca depois — é ele que o servidor lê. Prato antigo sem foto e sem
      o campo não paga nada. */
  /** "Comentários e notas" desligado por quem postou: vale em todo grupo. */
  fechado?: boolean;
  /** Quem postou foi removido deste grupo pelo dono. Só o servidor escreve. */
  oculto?: boolean;
  semFoto?: boolean;

  place: string;
  placeKey: string;
  coords: { lat: number; lng: number } | null;
  price: number | null;
  /** Onde foi comprado, quando a pessoa disse. Só em prato comprado. */
  canal?: Canal | null;
  /** Qual app de entrega, só com `canal: "delivery"`. */
  appDelivery?: AppDeDelivery | null;

  day: DayKey;
  at: Timestamp;
  createdAt: Timestamp;
  /** Data escolhida à mão vence a do servidor. */
  dateEdited: boolean;

  /* ---- o mesmo prato em vários desafios ----

     A cópia leva o MESMO id de documento do original. É o que faz achar
     as cópias ser só saber a lista de `cid`, sem consulta nem índice. */
  /** No ORIGINAL: os outros desafios onde ele também aparece. */
  replicas?: string[];
  /** Na CÓPIA: de onde ela veio. É esta marca que impede o gatilho de
      replicar uma réplica, e por isso só o servidor a escreve. */
  origemCid?: string;

  /* ---- escritos pelo servidor ---- */
  rulesVersion: number;
  basePoints: number;
  mealRepeated: boolean;
  ratingAvg: number | null;
  ratingCount: number;
  commentCount: number;
  /* Quantas pessoas deixaram cada emoji, para o feed desenhar o resumo
     sem ler a subcoleção de cada card. Ausente quando ninguém reagiu:
     mapa vazio é campo a mais em todo prato do banco. */
  reactions?: Record<string, number>;
  kcal: number | null;
  kcalSource: "ai" | "base" | "manual" | null;
  /** Faixa honesta: quanto menos dá pra ver na foto, mais larga. */
  kcalMin?: number | null;
  kcalMax?: number | null;
  kcalItems?: { nome: string; porcao: string; kcal: number }[];
  kcalConfidence?: "alta" | "media" | "baixa";
  kcalAt?: Timestamp;
  /** O que a IA achou que tem no prato (açúcar, fritura…). Dá os selos
      automáticos. Ausente/`null` = ainda não avaliado. */
  kcalMarcas?: Marca[] | null;
}

/** Uma nota. Só quem deu consegue ler o próprio documento. */
export interface RatingDoc {
  score: number;
  at: Timestamp;
}

/**
 * Uma reação. Um documento por pessoa, então uma reação por pessoa —
 * trocar de emoji substitui, e tocar no mesmo desfaz.
 *
 * Ao contrário da nota, é pública: reação sem nome não é reação, é
 * placar. Aqui o ponto é justamente saber quem achou graça.
 */
export interface ReactionDoc {
  emoji: string;
  at: Timestamp;
}

export interface GuessDoc {
  kind: "price" | "kcal";
  value: number;
  at: Timestamp;
  /** Preenchido pelo servidor quando cravou. */
  awarded?: number;
}

/* O número mora no core: o esquema cria a ficha, a função de gastar
   precisa dele para ficha antiga sem o campo, e a regra do Firestore o
   repete como literal porque não importa nada. Ver
   `packages/core/src/valeFalta.ts`. */
export const STARTING_JOKERS = VALE_FALTAS_INICIAIS;

/** Ficha zerada de quem acabou de entrar. O placar nasce vazio e é do servidor. */
export function emptyMember(
  name: string,
  photo: string,
  role: MemberDoc["role"],
): Omit<MemberDoc, "joinedAt"> {
  return {
    name, photo, role,
    days: [], dayPoints: {}, extraPoints: {}, totalPoints: 0,
    events: {}, cuisineIds: [],
    plateCount: 0, homemadeCount: 0, boughtCount: 0,
    jokerDays: [], jokers: STARTING_JOKERS,
  };
}
